import React, { useState, useEffect, useRef } from 'react';
import DatePicker, { CalendarContainer } from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { IoChevronBack, IoChevronForward } from 'react-icons/io5';
import theme from '../config/theme';

// DatePicker Period Props interface
export interface DatePickerPeriodProps {
  isOpen: boolean;
  onClose: () => void;
  onApply: (startDate: Date | null, endDate: Date | null) => void;
  startDate?: Date | null;
  endDate?: Date | null;
  minDate?: Date;
  maxDate?: Date;
  className?: string;
  anchorRef?: React.RefObject<HTMLElement>;
}

// DatePicker Period Component
const DatePickerPeriod: React.FC<DatePickerPeriodProps> = ({
  isOpen,
  onClose,
  onApply,
  startDate = null,
  endDate = null,
  minDate,
  maxDate,
  className = '',
  anchorRef,
}) => {
  const [dateRange, setDateRange] = useState<[Date | null, Date | null]>([
    startDate,
    endDate,
  ]);
  const [tempDateRange, setTempDateRange] = useState<[Date | null, Date | null]>([
    startDate,
    endDate,
  ]);
  const popoverRef = useRef<HTMLDivElement>(null);

  // Helper function to normalize date (remove time component for comparison)
  const normalizeDate = (date: Date | null): Date | null => {
    if (!date) return null;
    return new Date(date.getFullYear(), date.getMonth(), date.getDate());
  };

  // Update date range when props change
  useEffect(() => {
    // Normalize dates when props change to ensure consistency
    const normalizedStart = normalizeDate(startDate);
    const normalizedEnd = normalizeDate(endDate);
    setDateRange([normalizedStart, normalizedEnd]);
    setTempDateRange([normalizedStart, normalizedEnd]);
  }, [startDate, endDate]);

  // Close popover on escape key press
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };
    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
    }
    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, onClose]);

  // Close popover when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (popoverRef.current && !popoverRef.current.contains(event.target as Node)) {
        onClose();
      }
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose]);

  // If popover is not open, don't render anything
  if (!isOpen) return null;

  // Helper function to compare dates (only year, month, day)
  const isSameDate = (date1: Date, date2: Date): boolean => {
    return (
      date1.getFullYear() === date2.getFullYear() &&
      date1.getMonth() === date2.getMonth() &&
      date1.getDate() === date2.getDate()
    );
  };

  // Handle date range change
  const handleDateChange = (update: [Date | null, Date | null] | Date | null) => {
    // Handle both single date and date range
    if (Array.isArray(update)) {
      // Normalize dates to remove time component
      const normalizedUpdate: [Date | null, Date | null] = [
        update[0] ? normalizeDate(update[0]) : null,
        update[1] ? normalizeDate(update[1]) : null,
      ];
      setTempDateRange(normalizedUpdate);
    } else if (update instanceof Date) {
      // Single date selection (shouldn't happen with selectsRange, but handle it)
      const normalizedDate = normalizeDate(update);
      setTempDateRange([normalizedDate, null]);
    } else {
      // null value
      setTempDateRange([null, null]);
    }
  };

  // Handle apply
  const handleApply = () => {
    setDateRange(tempDateRange);
    onApply(tempDateRange[0], tempDateRange[1]);
    onClose();
  };

  // Handle cancel
  const handleCancel = () => {
    setTempDateRange(dateRange);
    onClose();
  };

  // Format date for input
  const formatDateForInput = (date: Date | null) => {
    if (!date) return '';
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    return `${day} / ${month} / ${year}`;
  };

  // Custom header renderer
  // When monthsShown > 1, react-datepicker calls this function for each month
  // The 'date' parameter should be the date of the month being displayed
  // For the second month, it should be the next month from the selected date
  const renderCustomHeader = ({
    date,
    decreaseMonth,
    increaseMonth,
    prevMonthButtonDisabled,
    nextMonthButtonDisabled,
    monthDate,
  }: any) => {
    const monthNames = [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'
    ];
    
    // Use monthDate if provided (for monthsShown > 1), otherwise use date
    // monthDate should be the actual date of the month being displayed
    const displayDate = monthDate ? new Date(monthDate.getFullYear(), monthDate.getMonth(), 1) : new Date(date.getFullYear(), date.getMonth(), 1);
    const monthName = monthNames[displayDate.getMonth()];

    return (
      <div
        className="flex items-center justify-between px-4 py-3 border-b"
        style={{
          borderColor: theme.color.light300,
          backgroundColor: theme.color.light100
        }}
      >
        <button
          type="button"
          onClick={decreaseMonth}
          disabled={prevMonthButtonDisabled}
          className="p-2 rounded-lg hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed border flex items-center justify-center"
          style={{ color: theme.color.gray500, borderColor: theme.color.light300 }}
        >
          {React.createElement(IoChevronBack as any, { size: 16 })}
        </button>
        <div className="font-semibold" style={{ color: theme.color.gray500 }}>
          {monthName}
        </div>
        <button
          type="button"
          onClick={increaseMonth}
          disabled={nextMonthButtonDisabled}
          className="p-2 rounded-lg hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed border flex items-center justify-center"
          style={{ color: theme.color.gray500, borderColor: theme.color.light300 }}
        >
          {React.createElement(IoChevronForward as any, { size: 16 })}
        </button>
      </div>
    );
  };

  // Custom calendar container
  const CustomCalendarContainer = ({ children }: { children: React.ReactNode }) => {
    return (
      <div className="custom-range-calendar-wrapper">
        {children}
      </div>
    );
  };

  // Calculate popover position
  const [popoverPosition, setPopoverPosition] = useState<{ top: number; left: number } | null>(null);

  useEffect(() => {
    if (isOpen && anchorRef?.current) {
      const anchorRect = anchorRef.current.getBoundingClientRect();
      const popoverWidth = 653; // Width of popover
      const popoverHeight = 500; // Approximate height

      let left = anchorRect.left;
      let top = anchorRect.bottom + 8;

      // Adjust if popover would go off right edge
      if (left + popoverWidth > window.innerWidth) {
        left = window.innerWidth - popoverWidth - 16;
      }

      // Adjust if popover would go off left edge
      if (left < 16) {
        left = 16;
      }

      // Adjust if popover would go off bottom edge
      if (top + popoverHeight > window.innerHeight) {
        top = anchorRect.top - popoverHeight - 8;
      }

      setPopoverPosition({
        top,
        left,
      });
    }
  }, [isOpen, anchorRef]);

  const content = (
    <div
      ref={popoverRef}
      className={`bg-white rounded-lg shadow-xl w-[653px] ${className}`}
      onClick={(e) => e.stopPropagation()}
      role="dialog"
      aria-labelledby="datepicker-title"
      style={
        popoverPosition
          ? {
            position: 'fixed',
            top: `${popoverPosition.top}px`,
            left: `${popoverPosition.left}px`,
            zIndex: 50,
          }
          : {}
      }
    >
      {/* DatePicker */}
      <div>
        <DatePicker
          selected={tempDateRange[0]}
          startDate={tempDateRange[0]}
          endDate={tempDateRange[1]}
          onChange={handleDateChange}
          selectsRange
          monthsShown={2}
          inline
          fixedHeight={false}
          minDate={minDate}
          maxDate={maxDate}
          renderCustomHeader={renderCustomHeader}
          calendarContainer={CustomCalendarContainer}
          formatWeekDay={(nameOfDay) => nameOfDay.substring(0, 2)}
          calendarClassName="custom-range-calendar"
          popperClassName="custom-range-popper"
          dayClassName={(date: Date) => {
            const start = tempDateRange[0];
            const end = tempDateRange[1];

            if (!start && !end) return '';

            // Normalize dates for comparison (remove time component)
            const normalizedDate = normalizeDate(date);
            const normalizedStart = normalizeDate(start);
            const normalizedEnd = normalizeDate(end);

            if (!normalizedDate) return '';

            // Check if date is start date
            if (normalizedStart && isSameDate(normalizedDate, normalizedStart)) {
              return 'range-start';
            }

            // Check if date is end date
            if (normalizedEnd && isSameDate(normalizedDate, normalizedEnd)) {
              return 'range-end';
            }

            // Check if date is in range
            if (normalizedStart && normalizedEnd && normalizedDate >= normalizedStart && normalizedDate <= normalizedEnd) {
              return 'in-range';
            }

            return '';
          }}
        />
      </div>

      {/* Footer Section with Date Inputs and Buttons */}
      <div
        className="flex items-center rounded-b-lg justify-between px-4 py-3 border-t"
        style={{
          backgroundColor: theme.color.light100,
          borderColor: theme.color.light300
        }}
      >
        {/* Date Input Fields */}
        <div className="flex items-center gap-4 flex-1">
          <div className="flex items-center gap-2 max-w-xs">
            <input
              type="text"
              value={formatDateForInput(tempDateRange[0])}
              placeholder="Start Date"
              readOnly
              className="w-[130px] px-3 py-2 border rounded-lg text-gray-900 cursor-not-allowed"
              style={{
                borderColor: theme.color.light500,
                backgroundColor: theme.color.light100,
                color: theme.color.gray500
              }}
            />
            <span className="font-medium" style={{ color: theme.color.gray500 }}>To</span>
            <input
              type="text"
              value={formatDateForInput(tempDateRange[1])}
              placeholder="End Date"
              readOnly
              className="w-[130px] px-3 py-2 border rounded-lg text-gray-900 cursor-not-allowed"
              style={{
                borderColor: theme.color.light500,
                backgroundColor: theme.color.light100,
                color: theme.color.gray500
              }}
            />
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex justify-end gap-3">
          <button
            type="button"
            onClick={handleCancel}
            className="px-6 py-2 rounded-lg border text-sm font-medium transition-all"
            style={{
              borderColor: theme.color.light500,
              backgroundColor: theme.color.light100,
              color: theme.color.gray500
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.opacity = '0.9';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.opacity = '1';
            }}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleApply}
            className="px-6 py-2 rounded-lg border-0 text-sm font-medium text-white transition-all"
            style={{
              backgroundColor: theme.brandColor.red,
              color: theme.color.light100
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.opacity = '0.9';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.opacity = '1';
            }}
          >
            Apply
          </button>
        </div>
      </div>


      <style>{`
  .custom-range-calendar-wrapper {
    display: flex !important;
    flex-direction: row !important;
    background: white !important;
    border-radius: 0.5rem !important;
    box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05) !important;
    overflow: hidden !important;
  }
  .custom-range-calendar {
    border: none !important;
    border-radius: 0 !important;
    box-shadow: none !important;
    overflow: visible !important;
    background: white !important;
    padding: 0 !important;
    display: flex !important;
    flex-direction: row !important;
    width: 100% !important;
  }
  .react-datepicker__month-wrapper {
    display: flex !important;
    flex-direction: row !important;
    width: 100% !important;
  }
  .react-datepicker__header {
    background-color: white !important;
    border-bottom: none !important;
    padding: 0 !important;
  }
  .react-datepicker__header .react-datepicker__current-month {
    display: none !important;
  }
  .react-datepicker__month-container {
    padding: 0 !important;
  }
  .react-datepicker__day-names {
    display: flex !important;
    justify-content: space-between !important;
    padding: 0.5rem 1rem !important;
    border-bottom: 1px solid ${theme.color.light300} !important;
    margin: 0 !important;
  }
  .react-datepicker__day-name {
    color: ${theme.color.gray300} !important;
    font-size: 0.875rem !important;
    font-weight: 500 !important;
    width: 2rem !important;
    margin: 0 !important;
    text-transform: capitalize !important;
  }
  .react-datepicker__month {
    padding: 0.5rem 1rem !important;
    margin: 0 !important;
  }
  .react-datepicker__week {
    display: flex !important;
    justify-content: space-between !important;
  }
  .react-datepicker__day {
    width: 2rem !important;
    height: 2rem !important;
    line-height: 2rem !important;
    margin: 0.25rem 0 !important;
    border-radius: 50% !important;
    color: ${theme.color.gray500} !important;
    font-size: 0.875rem !important;
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
  }
  .react-datepicker__day:hover {
    background-color: ${theme.color.light200} !important;
    border-radius: 50% !important;
  }
  .react-datepicker__day--selected,
  .range-start,
  .range-end {
    background-color: ${theme.brandColor.red} !important;
    color: white !important;
    border-radius: 50% !important;
    font-weight: 600 !important;
  }
  .react-datepicker__day--in-range,
  .in-range {
    background-color: rgba(215, 25, 32, 0.3) !important;
    color: ${theme.color.gray500} !important;
    border-radius: 50% !important;
  }
  .react-datepicker__day--in-range:hover {
    background-color: rgba(215, 25, 32, 0.4) !important;
  }
  .react-datepicker__day--range-start,
  .react-datepicker__day--range-end,
  .range-start,
  .range-end {
    background-color: ${theme.brandColor.red} !important;
    color: white !important;
    border-radius: 50% !important;
    font-weight: 600 !important;
  }
  .react-datepicker__day--in-selecting-range {
    background-color: rgba(215, 25, 32, 0.2) !important;
    color: ${theme.color.gray500} !important;
  }
  .react-datepicker__day--disabled {
    color: ${theme.color.gray200} !important;
    cursor: not-allowed !important;
  }
  .custom-range-popper {
    z-index: 50 !important;
  }
  .react-datepicker {
    display: flex !important;
    flex-direction: row !important;
    width: 100% !important;
  }
  .react-datepicker__month-container {
    float: none !important;
    width: 50% !important;
    flex: 0 0 50% !important;
  }
  .react-datepicker__month-container {
    padding: 0 !important;
  }
  .react-datepicker__month-container:first-child {
    border-right: 1px solid ${theme.color.light300} !important;
    padding-right: 1rem !important;
  }
  .react-datepicker__month-container:last-child {
    padding-left: 1rem !important;
  }
`}</style>
    </div>
  );

  return isOpen ? (
    <>
      {/* Transparent backdrop for popover - only to catch clicks outside */}
      <div
        className="fixed inset-0 z-40"
        onClick={onClose}
        style={{ backgroundColor: 'transparent' }}
      />
      {content}
    </>
  ) : null;
};

export default DatePickerPeriod;
